import { useEffect, useState, useCallback, useRef } from 'react';
import { videosAPI, videoPlaylistsAPI, usersAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

const VIDEO_ACCEPT = '.mp4,.webm,.ogv,.mov,.avi,.mkv';
const THUMB_ACCEPT = '.jpg,.jpeg,.png,.webp';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
function fmtDuration(s) {
  if (!s) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec < 10 ? '0' + sec : sec}`;
}
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: 'var(--muted)' };
const tabStyle = (active) => ({
  padding: '8px 20px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
  color: active ? 'var(--accent)' : 'var(--text2)',
  transition: 'color .15s',
});

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Video() {
  const [tab, setTab] = useState('videos'); // 'videos' | 'playlists'

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Video Library</h1>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
          Manage videos and playlists. Role-based access controls who can watch.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        <button style={tabStyle(tab === 'videos')}    onClick={() => setTab('videos')}>🎬 Videos</button>
        <button style={tabStyle(tab === 'playlists')} onClick={() => setTab('playlists')}>📂 Playlists</button>
      </div>

      {tab === 'videos' ? <VideosTab /> : <PlaylistsTab />}
    </div>
  );
}

// ── Videos Tab ────────────────────────────────────────────────────────────────
function VideosTab() {
  const { show, ToastEl } = useToast();
  const [videos, setVideos]         = useState([]);
  const [roles, setRoles]           = useState([]);
  const [playlists, setPlaylists]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [meta, setMeta]             = useState({});
  const [statusFilter, setStatus]   = useState('');
  const [playlistFilter, setPlFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editVideo, setEditVideo]   = useState(null);
  const [previewId, setPreviewId]         = useState(null);
  const [commentsVideoId, setCommentsId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await videosAPI.list({
        page, limit: 15,
        status: statusFilter || undefined,
        playlist_id: playlistFilter || undefined,
      });
      setVideos(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load videos', 'error'); }
    finally { setLoading(false); }
  }, [page, statusFilter, playlistFilter]);

  useEffect(() => {
    usersAPI.listRoles().then(r => setRoles(r.data.data)).catch(() => {});
    videoPlaylistsAPI.list({ limit: 100 }).then(r => setPlaylists(r.data.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function del(id, title) {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try { await videosAPI.delete(id); show('Video deleted'); load(); }
    catch (e) { show(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Title', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 72, height: 44, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
          background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {row.thumbnail_path
            ? <img src={videosAPI.thumbnailUrl(row.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 20 }}>🎬</span>
          }
        </div>
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{v}</div>
          {row.description && (
            <div style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.description}
            </div>
          )}
        </div>
      </div>
    )},
    { key: 'playlist_title', label: 'Playlist', render: v => v
      ? <span style={{ fontSize: 12, background: 'var(--accent2)', color: '#fff', padding: '2px 8px', borderRadius: 10 }}>{v}</span>
      : <span style={{ fontSize: 11, color: 'var(--muted)' }}>—</span>
    },
    { key: 'duration_seconds', label: 'Duration', render: fmtDuration },
    { key: 'file_size_bytes',  label: 'Size',     render: formatBytes },
    { key: 'view_count',  label: 'Views',  render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'like_count',  label: 'Likes',  render: (_, row) => <LikeCount videoId={row.id} /> },
    { key: 'sort_order',  label: 'Order',  render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'status',      label: 'Status', render: v => <Badge label={v} type={v} /> },
    { key: 'role_ids',    label: 'Roles',  render: (v, _, roles) => (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {(v || []).map(rid => {
          const r = roles?.find(x => x.id === rid);
          return r ? <Badge key={rid} label={r.name} type="role_specific" /> : null;
        })}
      </div>
    )},
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 5 }}>
        <Btn size="sm" onClick={() => setPreviewId(row.id)}>▶ Play</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setCommentsId(row.id)}>💬</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setEditVideo(row)}>Edit</Btn>
        <Btn size="sm" variant="danger" onClick={() => del(row.id, row.title)}>Delete</Btn>
      </div>
    )},
  ];

  // Inject roles into render via closure
  const columnsWithRoles = columns.map(c =>
    c.key === 'role_ids'
      ? { ...c, render: (v) => c.render(v, null, roles) }
      : c
  );

  return (
    <>
      {ToastEl}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Select
            value={statusFilter}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'published', label: 'Published' },
              { value: 'draft', label: 'Draft' },
              { value: 'archived', label: 'Archived' },
            ]}
            style={{ width: 160 }}
          />
          <Select
            value={playlistFilter}
            onChange={e => { setPlFilter(e.target.value); setPage(1); }}
            options={[
              { value: '', label: 'All playlists' },
              { value: 'none', label: '— No playlist' },
              ...playlists.map(p => ({ value: p.id, label: p.title })),
            ]}
            style={{ width: 200 }}
          />
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ Upload Video</Btn>
      </div>

      <Card>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
          : <Table columns={columnsWithRoles} data={videos} emptyMsg="No videos found." />
        }
        {!loading && meta.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <Pagination page={page} pages={meta.pages} onChange={setPage} />
          </div>
        )}
      </Card>

      {previewId && <VideoPreviewModal videoId={previewId} onClose={() => setPreviewId(null)} />}

      {commentsVideoId && (
        <VideoCommentsModal videoId={commentsVideoId} onClose={() => setCommentsId(null)} />
      )}

      {showCreate && (
        <VideoFormModal
          roles={roles}
          playlists={playlists}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); show('Video uploaded'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {editVideo && (
        <VideoFormModal
          video={editVideo}
          roles={roles}
          playlists={playlists}
          onClose={() => setEditVideo(null)}
          onSuccess={() => { setEditVideo(null); load(); show('Video updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </>
  );
}

// ── Playlists Tab ─────────────────────────────────────────────────────────────
function PlaylistsTab() {
  const { show, ToastEl } = useToast();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [meta, setMeta]           = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editPlaylist, setEdit]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await videoPlaylistsAPI.list({ page, limit: 15 });
      setPlaylists(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load playlists', 'error'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  async function del(id, title) {
    if (!window.confirm(`Delete playlist "${title}"?\nVideos in this playlist will be unlinked but not deleted.`)) return;
    try { await videoPlaylistsAPI.delete(id); show('Playlist deleted'); load(); }
    catch (e) { show(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Playlist', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 52, height: 36, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
          background: 'var(--accent2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {row.cover_path
            ? <img src={videoPlaylistsAPI.coverUrl(row.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 18 }}>📂</span>
          }
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
          {row.description && (
            <div style={{ fontSize: 11, color: 'var(--muted)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.description}
            </div>
          )}
        </div>
      </div>
    )},
    { key: 'video_count', label: 'Videos',     render: v => <span style={{ fontSize: 13, fontWeight: 600 }}>{v ?? 0}</span> },
    { key: 'sort_order',  label: 'Order',      render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'status',      label: 'Status',     render: v => <Badge label={v} type={v} /> },
    { key: 'created_by',  label: 'Created by', render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 5 }}>
        <Btn size="sm" variant="ghost" onClick={() => setEdit(row)}>Edit</Btn>
        <Btn size="sm" variant="danger" onClick={() => del(row.id, row.title)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <>
      {ToastEl}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn onClick={() => setShowCreate(true)}>+ New Playlist</Btn>
      </div>

      <Card>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
          : <Table columns={columns} data={playlists} emptyMsg="No playlists yet. Create one to start grouping videos." />
        }
        {!loading && meta.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <Pagination page={page} pages={meta.pages} onChange={setPage} />
          </div>
        )}
      </Card>

      {showCreate && (
        <PlaylistFormModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); show('Playlist created'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
      {editPlaylist && (
        <PlaylistFormModal
          playlist={editPlaylist}
          onClose={() => setEdit(null)}
          onSuccess={() => { setEdit(null); load(); show('Playlist updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </>
  );
}

// ── Video preview modal ───────────────────────────────────────────────────────
function VideoPreviewModal({ videoId, onClose }) {
  const token = localStorage.getItem('accessToken');
  const src   = `${videosAPI.streamUrl(videoId)}?token=${token}`;

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: '#000b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: 12, width: '90vw', maxWidth: 900, overflow: 'hidden', boxShadow: '0 24px 60px #0006' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Video Preview</span>
          <Btn size="sm" variant="ghost" onClick={onClose}>✕ Close</Btn>
        </div>
        <div style={{ background: '#000' }}>
          <video src={src} controls autoPlay style={{ width: '100%', maxHeight: '72vh', display: 'block' }} />
        </div>
      </div>
    </div>
  );
}

// ── Inline like count ─────────────────────────────────────────────────────────
function LikeCount({ videoId }) {
  const [count, setCount] = useState('…');
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/videos/${videoId}/likes`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    })
      .then(r => r.json())
      .then(d => setCount(d.data?.count ?? 0))
      .catch(() => setCount('—'));
  }, [videoId]);
  return <span style={{ fontSize: 12 }}>👍 {count}</span>;
}

// ── Comments moderation modal ─────────────────────────────────────────────────
function VideoCommentsModal({ videoId, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [meta, setMeta]         = useState({});
  const { show, ToastEl }       = useToast();

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await videosAPI.listComments(videoId, { page: p, limit: 20 });
      setComments(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load comments', 'error'); }
    finally { setLoading(false); }
  }, [videoId, page]);

  useEffect(() => { load(page); }, [page]);

  async function del(commentId) {
    if (!window.confirm('Delete this comment?')) return;
    try { await videosAPI.deleteComment(videoId, commentId); show('Comment deleted'); load(page); }
    catch { show('Failed to delete', 'error'); }
  }

  return (
    <Modal title="Video Comments" onClose={onClose} width={600}>
      {ToastEl}
      {loading
        ? <div style={{ padding: 32, textAlign: 'center' }}><Spinner /></div>
        : comments.length === 0
          ? <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 24 }}>No comments yet.</p>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {comments.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {c.user_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{c.user_name}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{c.comment}</p>
                  </div>
                  <Btn size="sm" variant="danger" onClick={() => del(c.id)}>Delete</Btn>
                </div>
              ))}
              {meta.pages > 1 && (
                <div style={{ paddingTop: 12 }}>
                  <Pagination page={page} pages={meta.pages} onChange={p => setPage(p)} />
                </div>
              )}
            </div>
          )
      }
    </Modal>
  );
}

// ── Video upload / edit modal ─────────────────────────────────────────────────
function VideoFormModal({ video, roles, playlists, onClose, onSuccess, onError }) {
  const isEdit = !!video;
  const [title, setTitle]           = useState(video?.title || '');
  const [description, setDesc]      = useState(video?.description || '');
  const [status, setStatus]         = useState(video?.status || 'published');
  const [sortOrder, setSortOrder]   = useState(video?.sort_order ?? 0);
  const [playlistId, setPlaylistId] = useState(video?.playlist_id != null ? String(video.playlist_id) : '');
  const [selectedRoles, setSelRoles]= useState(video?.role_ids || roles.map(r => r.id));
  const [videoFile, setVideoFile]   = useState(null);
  const [thumbFile, setThumbFile]   = useState(null);
  const [saving, setSaving]         = useState(false);
  const [progress, setProgress]     = useState(0);

  const videoRef = useRef(null);
  const thumbRef = useRef(null);

  function toggleRole(id) {
    setSelRoles(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  }

  async function submit(e) {
    e.preventDefault();
    if (!isEdit && !videoFile) { onError('Please select a video file'); return; }
    if (!title.trim()) { onError('Title is required'); return; }
    if (!selectedRoles.length) { onError('Select at least one role'); return; }

    setSaving(true); setProgress(0);
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description);
      form.append('status', status);
      form.append('sort_order', sortOrder);
      form.append('playlist_id', playlistId || '');
      selectedRoles.forEach(id => form.append('roleIds[]', id));
      if (videoFile) form.append('video', videoFile);
      if (thumbFile) form.append('thumbnail', thumbFile);

      if (isEdit) {
        await videosAPI.update(video.id, form);
      } else {
        await videosAPI.create(form, ev => setProgress(Math.round((ev.loaded / ev.total) * 100)));
      }
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || (isEdit ? 'Update failed' : 'Upload failed'));
    } finally { setSaving(false); setProgress(0); }
  }

  return (
    <Modal title={isEdit ? 'Edit Video' : 'Upload Video'} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Video file picker */}
          <div>
            <label style={labelStyle}>VIDEO FILE {!isEdit && '*'}</label>
            <div
              onClick={() => videoRef.current?.click()}
              style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: '16px 12px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg)', color: videoFile ? 'var(--text)' : 'var(--muted)', fontSize: 13 }}
            >
              {videoFile ? `✓ ${videoFile.name}` : isEdit ? 'Click to replace video file (optional)' : 'Click to pick a video file (mp4, webm, mov…)'}
            </div>
            <input ref={videoRef} type="file" accept={VIDEO_ACCEPT} style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (!f) return;
                setVideoFile(f);
                if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
              }}
            />
          </div>

          {/* Thumbnail picker */}
          <div>
            <label style={labelStyle}>THUMBNAIL IMAGE (optional)</label>
            <div
              onClick={() => thumbRef.current?.click()}
              style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: '12px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg)', color: thumbFile ? 'var(--text)' : 'var(--muted)', fontSize: 13 }}
            >
              {thumbFile ? `✓ ${thumbFile.name}` : isEdit && video?.thumbnail_path ? '✓ Current thumbnail (click to replace)' : 'Click to pick a thumbnail image'}
            </div>
            <input ref={thumbRef} type="file" accept={THUMB_ACCEPT} style={{ display: 'none' }}
              onChange={e => setThumbFile(e.target.files?.[0] || null)}
            />
          </div>

          <Input label="Title *" value={title} onChange={e => setTitle(e.target.value)} placeholder="Enter video title" required />
          <Input label="Description" value={description} onChange={e => setDesc(e.target.value)} placeholder="Optional description" />

          {/* Playlist */}
          <Select
            label="Playlist (optional)"
            value={playlistId}
            onChange={e => setPlaylistId(e.target.value)}
            options={[
              { value: '', label: '— No playlist —' },
              ...playlists.map(p => ({ value: String(p.id), label: p.title })),
            ]}
          />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value)}
                options={[{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="Sort Order" type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} min={0} />
            </div>
          </div>

          {/* Role access */}
          <div>
            <label style={labelStyle}>ACCESS — WHO CAN WATCH *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {roles.map(r => (
                <button key={r.id} type="button" onClick={() => toggleRole(r.id)} style={{
                  padding: '4px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: selectedRoles.includes(r.id) ? 'var(--accent)' : 'transparent',
                  color: selectedRoles.includes(r.id) ? '#fff' : 'var(--text)',
                }}>
                  {r.name}
                </button>
              ))}
            </div>
          </div>

          {/* Upload progress */}
          {saving && !isEdit && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 3, background: 'var(--accent)', width: `${progress}%`, transition: 'width .2s ease' }} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Btn type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>
              {saving ? (isEdit ? 'Saving…' : `Uploading ${progress}%`) : (isEdit ? 'Save Changes' : 'Upload')}
            </Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ── Playlist create / edit modal ──────────────────────────────────────────────
function PlaylistFormModal({ playlist, onClose, onSuccess, onError }) {
  const isEdit = !!playlist;
  const [title, setTitle]         = useState(playlist?.title || '');
  const [description, setDesc]    = useState(playlist?.description || '');
  const [status, setStatus]       = useState(playlist?.status || 'published');
  const [sortOrder, setSortOrder] = useState(playlist?.sort_order ?? 0);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving]       = useState(false);
  const coverRef = useRef(null);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) { onError('Title is required'); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description);
      form.append('status', status);
      form.append('sort_order', sortOrder);
      if (coverFile) form.append('cover', coverFile);

      if (isEdit) {
        await videoPlaylistsAPI.update(playlist.id, form);
      } else {
        await videoPlaylistsAPI.create(form);
      }
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || (isEdit ? 'Update failed' : 'Create failed'));
    } finally { setSaving(false); }
  }

  return (
    <Modal title={isEdit ? 'Edit Playlist' : 'New Playlist'} onClose={onClose} width={480}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Cover image picker */}
          <div>
            <label style={labelStyle}>COVER IMAGE (optional)</label>
            <div
              onClick={() => coverRef.current?.click()}
              style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: '12px', textAlign: 'center', cursor: 'pointer', background: 'var(--bg)', color: coverFile ? 'var(--text)' : 'var(--muted)', fontSize: 13 }}
            >
              {coverFile ? `✓ ${coverFile.name}` : isEdit && playlist?.cover_path ? '✓ Current cover (click to replace)' : 'Click to pick a cover image'}
            </div>
            <input ref={coverRef} type="file" accept={THUMB_ACCEPT} style={{ display: 'none' }}
              onChange={e => setCoverFile(e.target.files?.[0] || null)}
            />
          </div>

          <Input label="Title *" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Friday Khutbah Series" required />
          <Input label="Description" value={description} onChange={e => setDesc(e.target.value)} placeholder="Optional description" />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value)}
                options={[{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="Sort Order" type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} min={0} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Btn type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Playlist')}</Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}
